import { useMemo } from "react";

import { Box, Flex, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useMask } from "@react-input/mask";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  DatePicker,
  GenderRadioGroup,
  InputLabel,

  Select,
  TextInput,
  TimeRangeSlider,
  YesNoRadioButton,
} from "components";
import { convertToTimeString } from "components/time";
import { AppConstants } from "constant";
import { getRequiredFieldStyles } from "pages/clients/view/components/required-field-styles";
import { CreditOptionsResponse, CreditRequestForm } from "types";
import { AppUtils } from "utils";

import SectionTitle, { SectionTitleIndicatorType } from "../../../components/section-title";

const { MASK_SWISS_PHONE_NUMBER } = AppConstants;

interface CreditRequestPersonalPartnerProps {
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
  isBankCredit?: boolean;
  hasWarning?: boolean;
  onWarningClick?: () => void;
  indicatorType?: SectionTitleIndicatorType;
}

const CreditRequestPersonalPartner = ({
  form,
  options,
  visibleFields,
  readOnly = false,
  isBankCredit = false,
  hasWarning = false,
  onWarningClick,
  indicatorType = "warning",
}: CreditRequestPersonalPartnerProps) => {
  const { t } = useTranslation();
  const availabilitySliderMaxWidth = 420;

  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options["nationality"], t),
    [options, t],
  );

  const getRequiredTextInputStyles = (
    fieldName: "partnerFirstName" | "partnerLastName" | "partnerEmail" | "partnerPhoneNumber",
  ) => {
    const value = form.getValues()?.[fieldName];
    const isPhoneField = fieldName === "partnerPhoneNumber";
    const isEmpty = isPhoneField
      ? typeof value !== "string" || value.replace(/\D/g, "").length === 0
      : typeof value !== "string" || value.trim() === "";

    return getRequiredFieldStyles({
      readOnly,
      isVisible: visibleFields.includes(fieldName),
      isEmpty,
    });
  };

  const getRequiredSelectStyles = (fieldName: "partnerNationality" | "partnerVisaType") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = typeof value !== "string" || value.trim() === "";

    return getRequiredFieldStyles({
      readOnly,
      isVisible: visibleFields.includes(fieldName),
      isEmpty,
    });
  };

  const getRequiredDatePickerStyles = (fieldName: "partnerBirthDate" | "partnerVisaFrom") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = !value;

    return getRequiredFieldStyles({
      readOnly,
      isVisible: visibleFields.includes(fieldName),
      isEmpty,
    });
  };

  return (
    <Box>
      <SectionTitle
        label={t("credit.partner.label")}
        isBankCredit={isBankCredit}
        hasWarning={hasWarning}
        onWarningClick={onWarningClick}
        indicatorType={indicatorType}
      />

      <SimpleGrid cols={4} mt={"sm"}>
        {visibleFields.includes("partnerOnSameAddress") && (
          <YesNoRadioButton
            label={t("credit.partnerOnSameAddress.label")}
            name={"partnerOnSameAddress"}
            form={form}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("partnerIncludeInApplication") && (
          <YesNoRadioButton
            label={t("credit.partnerIncludeInApplication.label")}
            name={"partnerIncludeInApplication"}
            form={form}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      {visibleFields.includes("partnerGender") && (
        <Box mb="md">
          <GenderRadioGroup
            label={t("credit.partnerGender.label")}
            fieldName="partnerGender"
            form={form}
            disabled={readOnly}
          />
        </Box>
      )}

      <SimpleGrid cols={4} mt={"sm"}>
        {visibleFields.includes("partnerFirstName") && (
          <TextInput
            label={t("credit.partnerFirstName.label")}
            key={form.key("partnerFirstName")}
            {...form.getInputProps("partnerFirstName")}
            styles={getRequiredTextInputStyles("partnerFirstName")}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("partnerLastName") && (
          <TextInput
            label={t("credit.partnerLastName.label")}
            key={form.key("partnerLastName")}
            {...form.getInputProps("partnerLastName")}
            styles={getRequiredTextInputStyles("partnerLastName")}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("partnerGender") && (
          <DatePicker
            label={t("credit.partnerBirthDate.label")}
            placeholder={t("user.birthDate.placeholder")}
            key={form.key("partnerBirthDate")}
            {...form.getInputProps("partnerBirthDate")}
            minDate={moment().subtract(65, "years")}
            maxDate={moment().subtract(18, "years")}
            styles={getRequiredDatePickerStyles("partnerBirthDate")}
            required={true}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <SimpleGrid cols={4} mt={"sm"}>
        {visibleFields.includes("partnerPhoneNumber") && (
          <Box>
            <InputLabel label={t("credit.partner.phone")} required={true} />
            <Flex justify={"space-between"}>
              <TextInput value={"+41"} w={"30%"} disabled={true} />
              <TextInput
                key={form.key("partnerPhoneNumber")}
                {...form.getInputProps("partnerPhoneNumber")}
                ref={phoneNumberRef}
                w={"65%"}
                styles={getRequiredTextInputStyles("partnerPhoneNumber")}
                required={true}
                disabled={readOnly}
              />
            </Flex>
          </Box>
        )}
        {visibleFields.includes("partnerEmail") && (
          <TextInput
            label={t("credit.partnerEmail.label")}
            key={form.key("partnerEmail")}
            {...form.getInputProps("partnerEmail")}
            styles={getRequiredTextInputStyles("partnerEmail")}
            required={true}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      {(visibleFields.includes("partnerWorkdayAvailabilityFrom") &&
        visibleFields.includes("partnerWorkdayAvailabilityTo")) ||
      (visibleFields.includes("partnerSaturdayAvailabilityFrom") &&
        visibleFields.includes("partnerSaturdayAvailabilityTo")) ? (
        <Box
          mt={"sm"}
          style={{
            alignItems: "flex-start",
            columnGap: "24px",
            display: "flex",
            flexWrap: "wrap",
            rowGap: "12px",
          }}
        >
          {visibleFields.includes("partnerWorkdayAvailabilityFrom") &&
            visibleFields.includes("partnerWorkdayAvailabilityTo") && (
              <Box style={{ maxWidth: availabilitySliderMaxWidth, width: "100%" }}>
                <InputLabel label={t("credit.workdayAvailability.label")} />
                <TimeRangeSlider
                  min={options["workdayAvailability"]?.min ?? 0}
                  max={options["workdayAvailability"]?.max ?? 23}
                  maxWidth={availabilitySliderMaxWidth}
                  valueFrom={form.values.partnerWorkdayAvailabilityFrom}
                  valueTo={form.values.partnerWorkdayAvailabilityTo}
                  disabled={readOnly}
                  onChangeEnd={(valueFrom, valueTo) => {
                    form.setFieldValue("partnerWorkdayAvailabilityFrom", valueFrom);
                    form.setFieldValue("partnerWorkdayAvailabilityTo", valueTo);
                  }}
                />
                <Checkbox
                  key={form.key("partnerSaturdayAvailability")}
                  label={t("credit.saturdayAvailability.label")}
                  {...form.getInputProps("partnerSaturdayAvailability", { type: "checkbox" })}
                  customOnClick={() => {
                    form.setFieldValue(
                      "partnerSaturdayAvailabilityFrom",
                      convertToTimeString(options["saturdayAvailability"]?.min || 0),
                    );
                    form.setFieldValue(
                      "partnerSaturdayAvailabilityTo",
                      convertToTimeString(options["saturdayAvailability"]?.max || 0),
                    );
                  }}
                  disabled={readOnly}
                  mt={"sm"}
                />
              </Box>
            )}

          {visibleFields.includes("partnerSaturdayAvailabilityFrom") &&
            visibleFields.includes("partnerSaturdayAvailabilityTo") && (
              <Box style={{ maxWidth: availabilitySliderMaxWidth, width: "100%" }}>
                <InputLabel label={t("credit.saturdayAvailability.label")} />
                <TimeRangeSlider
                  min={options["saturdayAvailability"]?.min ?? 0}
                  max={options["saturdayAvailability"]?.max ?? 23}
                  maxWidth={availabilitySliderMaxWidth}
                  valueFrom={form.values.partnerSaturdayAvailabilityFrom}
                  valueTo={form.values.partnerSaturdayAvailabilityTo}
                  disabled={readOnly}
                  onChangeEnd={(valueFrom, valueTo) => {
                    form.setFieldValue("partnerSaturdayAvailabilityFrom", valueFrom);
                    form.setFieldValue("partnerSaturdayAvailabilityTo", valueTo);
                  }}
                />
              </Box>
            )}
        </Box>
      ) : null}

      {visibleFields.includes("partnerNationality") && (
        <SimpleGrid cols={4} mt={"sm"}>
          <Select
            label={t("credit.partnerNationality.label")}
            data={nationalities}
            key={form.key("partnerNationality")}
            {...form.getInputProps("partnerNationality")}
            styles={getRequiredSelectStyles("partnerNationality")}
            searchable
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          {visibleFields.includes("partnerVisaType") && (
            <Select
              label={t("credit.partnerVisaType.label")}
              data={options["visaType"].map((el) => ({
                value: el,
                label: t(`user.visa.options.${el}`),
              }))}
              key={form.key("partnerVisaType")}
              {...form.getInputProps("partnerVisaType")}
              styles={getRequiredSelectStyles("partnerVisaType")}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes("partnerVisaFrom") && (
            <DatePicker
              label={t("credit.partnerVisaType.from")}
              key={form.key("partnerVisaFrom")}
              {...form.getInputProps("partnerVisaFrom")}
              styles={getRequiredDatePickerStyles("partnerVisaFrom")}
              maxDate={new Date()}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default CreditRequestPersonalPartner;
